import {
  DOCX_CONTENT_TYPE,
  FILE_ACCEPT,
  isImageContentType,
  isPdfContentType,
  type AllowedFileContentType
} from './constants';

/** Bytes needed to recognise every supported signature (WebP needs 12). */
export const SIGNATURE_BYTES = 16;

function ascii(bytes: Uint8Array, start: number, length: number): string {
  return String.fromCharCode(...bytes.slice(start, start + length));
}

/**
 * The content type implied by the leading bytes, or `undefined` when none of
 * the supported signatures match. Pure and browser-safe.
 */
export function sniffContentType(
  bytes: Uint8Array
): AllowedFileContentType | undefined {
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    bytes.length >= 8 &&
    [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every(
      (value, index) => bytes[index] === value
    )
  ) {
    return 'image/png';
  }
  if (ascii(bytes, 0, 6) === 'GIF87a' || ascii(bytes, 0, 6) === 'GIF89a') {
    return 'image/gif';
  }
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') {
    return 'image/webp';
  }
  if (ascii(bytes, 0, 5) === '%PDF-') {
    return 'application/pdf';
  }
  // DOCX files are ZIP archives; PK\x03\x04 is the ZIP local-file-header
  // signature. This matches any ZIP, but mammoth rejects non-DOCX content at
  // parse time and the file is never rendered directly.
  if (
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    bytes[2] === 0x03 &&
    bytes[3] === 0x04
  ) {
    return DOCX_CONTENT_TYPE;
  }
  return undefined;
}

/** ISO-BMFF brands that mean "HEIF/HEIC still image" (iPhone captures). */
const HEIC_BRANDS = new Set([
  'heic',
  'heix',
  'hevc',
  'hevx',
  'heim',
  'heis',
  'hevm',
  'hevs',
  'mif1',
  'msf1'
]);

/**
 * True for HEIC/HEIF containers: an ISO-BMFF `ftyp` box with a HEIF brand.
 * Kept out of `sniffContentType` on purpose; HEIC is converted, never stored.
 */
export function isHeicBytes(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 12 &&
    ascii(bytes, 4, 4) === 'ftyp' &&
    HEIC_BRANDS.has(ascii(bytes, 8, 4))
  );
}

/** True when `bytes` start with the signature expected for `contentType`. */
export function hasExpectedSignature(
  bytes: Uint8Array,
  contentType: AllowedFileContentType
): boolean {
  return sniffContentType(bytes) === contentType;
}

/**
 * Browsers derive `File.type` from the extension, so a PNG saved as
 * `menu.jpeg` arrives as `image/jpeg` and the server -- which checks the real
 * bytes -- rejects it. When the leading bytes unambiguously identify an image
 * or PDF that differs from the declared type, return a copy of the file with
 * the detected type and matching extension. ZIP-based (DOCX) detection is too
 * ambiguous to override a declared type, so it is left alone.
 */
export async function withDetectedContentType(file: File): Promise<File> {
  let head: Uint8Array;
  try {
    head = new Uint8Array(await file.slice(0, SIGNATURE_BYTES).arrayBuffer());
  } catch {
    return file;
  }
  const detected = sniffContentType(head);
  if (
    !detected ||
    detected === file.type ||
    !(isImageContentType(detected) || isPdfContentType(detected))
  ) {
    return file;
  }
  const extension = FILE_ACCEPT[detected]?.[0] ?? '';
  const base = file.name.replace(/\.[^./\\]+$/, '');
  return new File([file], `${base}${extension}`, {
    type: detected,
    lastModified: file.lastModified
  });
}
